import React, { useEffect, useState } from 'react';
import API_BASE from '../../utils/api.js';
import notify from '../../utils/notify';

const STATUS_COPY = {
  none: {
    dot: 'off',
    text: 'Not Connected',
    detail: 'Connect a Stripe account so clients can pay your invoices directly to your bank account.',
    buttonLabel: 'Set Up Payments',
  },
  pending: {
    dot: 'off',
    text: 'Setup In Progress',
    detail: "You've started onboarding but haven't finished. Resume where you left off.",
    buttonLabel: 'Resume Setup',
  },
  restricted: {
    dot: 'off',
    text: 'Action Needed',
    detail: 'Stripe needs more information before payments can resume.',
    buttonLabel: 'Resume Setup',
  },
  complete: {
    dot: 'on',
    text: 'Connected',
    detail: 'Clients paying your invoices are routed directly to your bank account.',
    buttonLabel: null,
  },
};

const FEE_DEFAULTS = {
  passProcessingFeesToClient: false,
  cardFeePct: 2.9,
  cardFeeFixed: 0.3,
  achFeePct: 0.8,
  achFeeCap: 5,
};

export default function StripeConnectSettings() {
  const apiUrl = API_BASE;
  const userId = localStorage.getItem('userId');

  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState('');

  const [feeSettings, setFeeSettings] = useState(FEE_DEFAULTS);
  const [feeLoading, setFeeLoading] = useState(true);
  const [feeSaving, setFeeSaving] = useState(false);

  const getToken = () => localStorage.getItem('idToken');

  async function fetchStatus() {
    const token = getToken();
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const res = await fetch(`${apiUrl}/api/stripe/connect/status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to load payment setup status.');
      const data = await res.json();
      setStatus(data);
    } catch (err) {
      console.error('Failed to fetch Stripe Connect status:', err);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchStatus();
  }, []);

  useEffect(() => {
    async function loadFeeSettings() {
      try {
        const res = await fetch(`${apiUrl}/api/settings/${userId}`);
        const data = await res.json();
        const pf = data.paymentFees || {};
        setFeeSettings({
          passProcessingFeesToClient: pf.passProcessingFeesToClient === true,
          cardFeePct: pf.cardFeePct ?? FEE_DEFAULTS.cardFeePct,
          cardFeeFixed: pf.cardFeeFixed ?? FEE_DEFAULTS.cardFeeFixed,
          achFeePct: pf.achFeePct ?? FEE_DEFAULTS.achFeePct,
          achFeeCap: pf.achFeeCap ?? FEE_DEFAULTS.achFeeCap,
        });
      } catch (err) {
        console.error('Failed to load payment fee settings', err);
      } finally {
        setFeeLoading(false);
      }
    }
    if (userId) loadFeeSettings();
  }, [apiUrl, userId]);

  const setFee = (key, value) => setFeeSettings((prev) => ({ ...prev, [key]: value }));

  async function saveFeeSettings() {
    setFeeSaving(true);
    try {
      const token = getToken();
      const res = await fetch(`${apiUrl}/api/settings/${userId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ paymentFees: feeSettings }),
      });
      if (!res.ok) throw new Error('Save failed');
      notify('Payment fee settings saved!');
    } catch (err) {
      console.error('Save payment fee settings failed', err);
      notify('Failed to save payment fee settings.');
    } finally {
      setFeeSaving(false);
    }
  }

  async function handleConnect(endpoint) {
    const token = getToken();
    if (!token) return;

    setConnecting(true);
    setError('');

    try {
      const res = await fetch(`${apiUrl}${endpoint}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.url) {
        throw new Error(data?.error || 'Failed to start Stripe onboarding.');
      }
      window.location.href = data.url;
    } catch (err) {
      setError(err.message || 'Failed to start Stripe onboarding.');
      setConnecting(false);
    }
  }

  const onboardingStatus = status?.onboardingStatus || 'none';
  const copy = STATUS_COPY[onboardingStatus] || STATUS_COPY.none;
  const connectEndpoint = onboardingStatus === 'none' ? '/api/stripe/connect/onboard' : '/api/stripe/connect/refresh-link';

  return (
    <div className="qb-settings-page">
      <h2 className="qb-title">Payments</h2>
      <p className="qb-subtitle">Accept card and ACH payments from clients, paid directly to your bank account via Stripe.</p>

      <div className="qb-card">
        <div className="qb-left">
          {loading ? (
            <div className="qb-skeleton">
              <div className="sk-row sk-title"></div>
              <div className="sk-row sk-line"></div>
              <div className="sk-btn"></div>
            </div>
          ) : (
            <>
              {status?.payoutIssue && (
                <div className="qb-detail" style={{ color: '#b91c1c', marginBottom: 14 }}>
                  A recent payout to your bank account failed. Check your bank details in Stripe or contact support.
                </div>
              )}

              <div className="qb-status-row">
                <span className={`qb-dot ${copy.dot}`}></span>
                <span className="qb-status-text">{copy.text}</span>
              </div>

              <div className="qb-info">{copy.detail}</div>

              {copy.buttonLabel && (
                <button
                  className={`qb-button connect${onboardingStatus === 'none' ? ' qb-pulse' : ''}`}
                  onClick={() => handleConnect(connectEndpoint)}
                  disabled={connecting}
                >
                  {connecting ? <span className="qb-spinner"></span> : copy.buttonLabel}
                </button>
              )}

              {error && <p style={{ color: '#b91c1c', marginTop: 12, fontSize: 13 }}>{error}</p>}
            </>
          )}
        </div>
      </div>

      <div className="qb-card" style={{ marginTop: 20 }}>
        <div className="qb-left" style={{ width: '100%' }}>
          <h3 style={{ margin: '0 0 8px', fontSize: 16 }}>Processing Fees</h3>
          <p className="qb-info" style={{ marginBottom: 16 }}>
            Pass Stripe's card/ACH processing cost on to the client instead of absorbing it yourself. The fee is shown
            as a separate line before the client confirms payment. Card surcharging is restricted or requires specific
            disclosures in some states — confirm this is compliant for your business before enabling.
          </p>

          {feeLoading ? (
            <div className="qb-skeleton"><div className="sk-row sk-line"></div></div>
          ) : (
            <>
              <label className="chk-label" style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
                <input
                  type="checkbox"
                  checked={feeSettings.passProcessingFeesToClient}
                  onChange={(e) => setFee('passProcessingFeesToClient', e.target.checked)}
                />
                Pass processing fees to the client
              </label>

              <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: 16, maxWidth: 480 }}>
                <div className="form-row">
                  <label>Card Fee %</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={feeSettings.cardFeePct}
                    onChange={(e) => setFee('cardFeePct', e.target.value)}
                    style={{ width: '100%' }}
                  />
                </div>
                <div className="form-row">
                  <label>Card Fee Fixed ($)</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={feeSettings.cardFeeFixed}
                    onChange={(e) => setFee('cardFeeFixed', e.target.value)}
                    style={{ width: '100%' }}
                  />
                </div>
                <div className="form-row">
                  <label>ACH Fee %</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={feeSettings.achFeePct}
                    onChange={(e) => setFee('achFeePct', e.target.value)}
                    style={{ width: '100%' }}
                  />
                </div>
                <div className="form-row">
                  <label>ACH Fee Cap ($)</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={feeSettings.achFeeCap}
                    onChange={(e) => setFee('achFeeCap', e.target.value)}
                    style={{ width: '100%' }}
                  />
                </div>
              </div>

              <button className="qb-button connect" style={{ marginTop: 16 }} onClick={saveFeeSettings} disabled={feeSaving}>
                {feeSaving ? 'Saving...' : 'Save'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
